import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import { GridRenderCellParams, GridValueFormatterParams } from '@mui/x-data-grid';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
import { formatCurrency } from 'utils/formatters/formatCurrency';
import { formatTimestamp } from 'utils/formatTimestamp';
import { convertCentsToDollars } from 'utils/convertMoney';
import { DEFAULT_PAGE_LIMIT } from 'utils/constants';
import {
  usePaymentDiscrepancyLogs,
  PaymentDiscrepancyLogListItem,
} from 'api/queries/usePaymentDiscrepancyLogs';
import { PaymentDiscrepancyStats } from './PaymentDiscrepancyStats';

const SOURCE_LABELS: Record<string, string> = {
  cron: 'Reconciliation Job',
  stripe_webhook: 'Stripe Webhook',
};

const TYPE_LABELS: Record<string, string> = {
  balance: 'Balance',
  tip: 'Tip',
};

const formatCentsColumn = ({ value }: GridValueFormatterParams<number>) =>
  formatCurrency(convertCentsToDollars(value ?? 0));

const formatSourceColumn = ({ value }: GridValueFormatterParams<string>) =>
  SOURCE_LABELS[value] ?? value;

const formatDateColumn = ({ value }: GridValueFormatterParams<string>) => formatTimestamp(value);

const renderTypeColumn = (params: GridRenderCellParams<PaymentDiscrepancyLogListItem>) => {
  const { discrepancyType } = params.row;

  if (!discrepancyType) {
    return <Chip size="small" color="default" label="Unknown" />;
  }

  return (
    <Chip size="small" color="default" label={TYPE_LABELS[discrepancyType] ?? discrepancyType} />
  );
};

const renderStatusColumn = (params: GridRenderCellParams<PaymentDiscrepancyLogListItem>) => {
  const { resolvedAt } = params.row;

  return resolvedAt ? (
    <Chip size="small" color="success" label="Resolved" />
  ) : (
    <Chip size="small" color="warning" label="Open" />
  );
};

const COLUMNS = [
  { headerName: 'ID', field: 'id', flex: 0.5, sortable: false },
  {
    headerName: 'Created At',
    field: 'createdAt',
    flex: 1.5,
    sortable: false,
    valueFormatter: formatDateColumn,
  },
  {
    headerName: 'Source',
    field: 'source',
    flex: 1,
    sortable: false,
    valueFormatter: formatSourceColumn,
  },
  {
    headerName: 'Type',
    field: 'discrepancyType',
    flex: 0.75,
    sortable: false,
    renderCell: renderTypeColumn,
  },
  {
    headerName: 'Status',
    field: 'resolvedAt',
    flex: 0.75,
    sortable: false,
    renderCell: renderStatusColumn,
  },
  {
    headerName: 'Amount Expected',
    field: 'netOrderTotalInCents',
    flex: 1,
    sortable: false,
    valueFormatter: formatCentsColumn,
  },
  {
    headerName: 'Amount Paid',
    field: 'paidAmountInCents',
    flex: 1,
    sortable: false,
    valueFormatter: formatCentsColumn,
  },
  {
    headerName: 'Balance Due',
    field: 'balanceDueInCents',
    flex: 1,
    sortable: false,
    valueFormatter: formatCentsColumn,
  },
];

const PaymentDiscrepancyLogsTable = () => {
  const history = useHistory();
  const [pageNumber, setPageNumber] = useState(0);

  const { data, isLoading } = usePaymentDiscrepancyLogs(pageNumber, DEFAULT_PAGE_LIMIT);

  const handleRowClick = (params: { row: PaymentDiscrepancyLogListItem }) => {
    history.push(`/payment-discrepancy-logs/${params.row.id}`);
  };

  return (
    <div className="page-container">
      <h1 className="payments-header">Payment Discrepancies</h1>
      <PaymentDiscrepancyStats />
      <CentsDataGridServer
        tableName="PAYMENT_DISCREPANCY_LOGS"
        columns={COLUMNS}
        data={data?.logs ?? []}
        rowCount={data?.total ?? 0}
        pageSize={DEFAULT_PAGE_LIMIT}
        page={pageNumber}
        onPageChange={({ page }: { page: number }) => setPageNumber(page)}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        getRowId={(row: PaymentDiscrepancyLogListItem) => row.id}
      />
    </div>
  );
};

export default PaymentDiscrepancyLogsTable;
